import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Heading, Input, Select, Stack, Text } from '@chakra-ui/react';
import AuthContext from '../context/AuthContext';
import Navbar from './Navbar';
import { API_BASE_URL } from '../config/adminConfig';

export default function ContactInbox() {
  const { token } = useContext(AuthContext);
  // Remount on account changes so one operator never sees another session's data.
  return <><Navbar /><Inbox key={token} token={token} /></>;
}

export function Inbox({ token }) {
  const [status, setStatus] = useState('new');
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [references, setReferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const saving = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    const controller = new AbortController();
    setItems([]); setLoading(true); setError('');
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/contact-requests?status=${status}&page=${page}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        if (!response.ok) throw new Error(response.status === 403 ? 'Administrator access required.' : 'Could not load the inbox.');
        const data = await response.json();
        if (!Array.isArray(data.items)) throw new Error('Invalid inbox response.');
        if (!controller.signal.aborted) setItems(data.items);
      } catch (e) { if (!controller.signal.aborted) setError(e.message); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    })();
    return () => controller.abort();
  }, [token, status, page, revision]);

  async function update(item, nextStatus) {
    const reference = (references[item._id] || '').trim();
    if (!reference || saving.current) return;
    saving.current = true; setBusy(item._id); setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact-requests/${encodeURIComponent(item._id)}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus, crmReference: reference }),
      });
      const receipt = await response.json();
      if (!response.ok || receipt.requestId !== item._id || receipt.status !== nextStatus || receipt.crmReference !== reference) {
        throw new Error('Update not confirmed. Refresh the inbox before retrying.');
      }
      if (mounted.current) setRevision(n => n + 1);
    } catch (e) { if (mounted.current) setError(e.message); }
    finally { saving.current = false; if (mounted.current) setBusy(''); }
  }

  return <Box p={{ base: 4, md: 8 }} maxW="1000px" mx="auto">
    <Heading mb={3}>Website contacts</Heading>
    <Text mb={5}>Review the request, record it in the canonical CRM, then enter its CRM reference here. Closing or importing a request does not send a message.</Text>
    <Stack direction={{ base: 'column', md: 'row' }} mb={5}>
      <Select aria-label="Contact status" value={status} isDisabled={!!busy} onChange={e => { setStatus(e.target.value); setPage(0); }}>
        <option value="new">New</option><option value="imported">Imported into CRM</option><option value="closed">Closed</option>
      </Select>
      <Button isDisabled={loading || !!busy} onClick={() => setRevision(n => n + 1)}>Refresh</Button>
    </Stack>
    {error && <Alert status="error" role="alert" mb={4}>{error}</Alert>}
    {loading ? <Text role="status">Loading contacts…</Text> : !items.length && <Text>No requests on this page.</Text>}
    <Stack spacing={5}>
      {items.map(item => <Box key={item._id} borderWidth="1px" borderRadius="md" p={4} overflowWrap="anywhere">
        <Heading size="sm">{item.payload?.contact?.name} — {item.payload?.contact?.company}</Heading>
        <Text>{item.payload?.contact?.email} {item.payload?.contact?.phone}</Text>
        <Text fontSize="sm">{item.receivedAt} · {item.payload?.intent} · {item.payload?.source}</Text>
        <Text my={3} whiteSpace="pre-wrap">{item.payload?.message}</Text>
        <Text fontSize="sm">Marketing consent: {item.payload?.marketing_consent === true ? 'Yes' : 'No'}</Text>
        <Text fontSize="sm">Request reference: {item._id}</Text>
        {item.crmReference && <Text>CRM reference: {item.crmReference}</Text>}
        {status === 'new' && <Stack mt={3}>
          <Input aria-label={`CRM reference for ${item._id}`} placeholder="Canonical CRM reference / closure note" maxLength={200}
            value={references[item._id] || ''} onChange={e => setReferences(s => ({ ...s, [item._id]: e.target.value }))} />
          <Stack direction="row">
            <Button isDisabled={!!busy || !(references[item._id] || '').trim()} onClick={() => update(item, 'imported')}>Mark imported</Button>
            <Button isDisabled={!!busy || !(references[item._id] || '').trim()} onClick={() => update(item, 'closed')}>Close request</Button>
          </Stack>
        </Stack>}
      </Box>)}
    </Stack>
    <Stack direction="row" mt={5}>
      <Button isDisabled={!page || loading || !!busy} onClick={() => setPage(n => n - 1)}>Previous</Button>
      <Text alignSelf="center">Page {page + 1}</Text>
      <Button isDisabled={items.length < 100 || loading || !!busy || !!error} onClick={() => setPage(n => n + 1)}>Next</Button>
    </Stack>
  </Box>;
}
